import React from 'react';
import { CalendarDays, MapPin, DollarSign, Wallet, Receipt, AlertCircle, Briefcase } from 'lucide-react';
import ToolResult, { ResultItem } from '../../../../../components/ui/ToolResult';
import { formatNumber, formatCurrency, formatDate } from '../../../../../utils/formatting';

/**
 * Компонент отображения результатов расчета суточных
 */
const ResultsOverview = ({ results }) => {
  if (!results) return null;

  // Форматирование даты
  const formatDateStr = (dateObj) => {
    if (!dateObj) return '';
    return formatDate(dateObj, 'date');
  };

  // Рассчитываем процент превышения норматива
  const excessPercentage = results.standardPerDiemTotal > 0 
    ? ((results.totalPerDiem - results.standardPerDiemTotal) / results.standardPerDiemTotal) * 100 
    : 0;

  return (
    <ToolResult title="Результаты расчета командировочных">
      {/* Ключевые метрики */}
      <div className="grid grid-cols-1 md:grid-cols-2 gap-5 mb-6">
        {/* Итоговая сумма */}
        <div className="rounded-lg bg-gradient-to-br from-indigo-50 to-purple-50 dark:from-indigo-900/30 dark:to-purple-900/30 p-5 border border-indigo-100 dark:border-indigo-800/50 shadow-sm">
          <h3 className="text-base font-medium text-indigo-700 dark:text-indigo-300 mb-1.5 flex items-center gap-1.5">
            <Wallet className="w-4 h-4"/>
            Итого к выплате
          </h3>
          <div className="flex items-baseline flex-wrap gap-x-2">
            <span className="text-3xl font-bold text-indigo-800 dark:text-indigo-200" aria-live="polite">
              {formatCurrency(results.netPayment)}
            </span>
          </div>
          <p className="text-xs text-indigo-600 dark:text-indigo-400 mt-1">
            Общая сумма с учетом удержания НДФЛ {results.advancePayment > 0 ? 'и выданного аванса' : ''}
          </p>
        </div>

        {/* Суточные и налоги */}
        <div className="rounded-lg bg-gradient-to-br from-green-50 to-blue-50 dark:from-green-900/30 dark:to-blue-900/30 p-5 border border-green-100 dark:border-green-800/50 shadow-sm">
          <h3 className="text-base font-medium text-green-700 dark:text-green-300 mb-1.5 flex items-center gap-1.5">
            <Receipt className="w-4 h-4"/>
            Общие расходы
          </h3>
          <div className="flex items-baseline flex-wrap gap-x-2">
            <span className="text-3xl font-bold text-green-800 dark:text-green-200" aria-live="polite">
              {formatCurrency(results.totalExpenses)}
            </span>
          </div>
          <p className="text-xs text-green-600 dark:text-green-400 mt-1">
            Включая суточные, проезд, проживание и доп. расходы
          </p>
        </div>
      </div>

      {/* Детализированные результаты */}
      <div className="space-y-3 mb-6">
        {/* Информация о командировке */}
        <div className="bg-gray-50 dark:bg-gray-800/50 rounded-lg p-4 border border-gray-200 dark:border-gray-700 mb-4">
          <h4 className="text-sm font-semibold text-gray-900 dark:text-white mb-2 flex items-center gap-2">
            <Briefcase className="w-4 h-4 text-indigo-500" />
            Информация о командировке
          </h4>
          
          <div className="grid grid-cols-1 md:grid-cols-2 gap-2 text-sm">
            <div className="flex items-start gap-2">
              <MapPin className="w-4 h-4 text-indigo-500 mt-0.5 flex-shrink-0" />
              <div>
                <span className="text-gray-500 dark:text-gray-400">Место назначения:</span>
                <span className="ml-1 font-medium text-gray-800 dark:text-gray-200">
                  {results.destinationName} ({results.destinationType === 'domestic' ? 'По России' : 'Зарубежная'})
                </span>
              </div>
            </div>
            
            <div className="flex items-start gap-2">
              <CalendarDays className="w-4 h-4 text-indigo-500 mt-0.5 flex-shrink-0" />
              <div>
                <span className="text-gray-500 dark:text-gray-400">Период:</span>
                <span className="ml-1 font-medium text-gray-800 dark:text-gray-200">
                  с {formatDateStr(results.tripStartDate)} по {formatDateStr(results.tripEndDate)} ({results.tripDurationDays} дн.)
                </span>
              </div>
            </div>
            
            <div className="flex items-start gap-2">
              <Briefcase className="w-4 h-4 text-indigo-500 mt-0.5 flex-shrink-0" />
              <div>
                <span className="text-gray-500 dark:text-gray-400">Цель:</span>
                <span className="ml-1 font-medium text-gray-800 dark:text-gray-200">
                  {results.businessPurposeName}
                </span>
              </div>
            </div>
            
            <div className="flex items-start gap-2">
              <Receipt className="w-4 h-4 text-indigo-500 mt-0.5 flex-shrink-0" />
              <div>
                <span className="text-gray-500 dark:text-gray-400">Документы:</span>
                <span className="ml-1 font-medium text-gray-800 dark:text-gray-200">
                  {results.hasDocuments ? results.documentsList : 'Отсутствуют'}
                </span>
              </div>
            </div>
          </div>
        </div>
        
        {/* Основные расходы */}
        <ResultItem 
          label="Суточные" 
          value={`${formatCurrency(results.totalPerDiem)} (${results.dailyRate} ₽ × ${results.tripDurationDays} дн.)`} 
          color="indigo" 
          icon={<Wallet className="w-4 h-4"/>}
        />
        
        {results.transportCost > 0 && (
          <ResultItem 
            label="Расходы на транспорт" 
            value={formatCurrency(results.transportCost)} 
            color="blue" 
            icon={<DollarSign className="w-4 h-4"/>}
          />
        )}
        
        {results.totalAccommodationCost > 0 && (
          <ResultItem 
            label="Расходы на проживание" 
            value={`${formatCurrency(results.totalAccommodationCost)} (${results.accommodationCostPerDay} ₽ × ${results.accommodationDays} ночей)`} 
            color="blue" 
            icon={<DollarSign className="w-4 h-4"/>}
          />
        )}
        
        {results.additionalCost > 0 && (
          <ResultItem 
            label={`Дополнительные расходы${results.additionalDescription ? ': ' + results.additionalDescription : ''}`} 
            value={formatCurrency(results.additionalCost)} 
            color="blue" 
            icon={<DollarSign className="w-4 h-4"/>}
          />
        )}
        
        <ResultItem 
          label="Общая сумма расходов" 
          value={formatCurrency(results.totalExpenses)} 
          color="green" 
        />
        
        {/* Нормативы и превышение */}
        <ResultItem 
          label="Норматив суточных" 
          value={`${formatCurrency(results.standardPerDiemTotal)} (${results.standardDailyRate} ₽ × ${results.tripDurationDays} дн.)`} 
          color="gray" 
        />
        
        {results.perDiemExcess > 0 && (
          <ResultItem 
            label="Превышение норматива суточных" 
            value={`${formatCurrency(results.perDiemExcess)} (+${formatNumber(excessPercentage, 1)}%)`} 
            color="orange" 
          />
        )}
        
        {results.incomeTax > 0 && (
          <ResultItem 
            label="НДФЛ с превышения норматива (13%)" 
            value={formatCurrency(results.incomeTax)} 
            color="red" 
          />
        )}
        
        {results.advancePayment > 0 && (
          <ResultItem 
            label="Выданный аванс" 
            value={formatCurrency(results.advancePayment)} 
            color="gray" 
          />
        )}
        
        <ResultItem 
          label="Итого к выплате" 
          value={formatCurrency(results.netPayment)} 
          color="indigo" 
          large={true}
        />

        {/* Информация о налогообложении */}
        {results.perDiemExcess > 0 && (
          <div className="bg-amber-50 dark:bg-amber-900/20 rounded-lg p-4 mt-2 border border-amber-200 dark:border-amber-800">
            <div className="flex items-start gap-3">
              <div className="mt-0.5">
                <AlertCircle className="w-5 h-5 text-amber-500" />
              </div>
              <div>
                <h4 className="text-sm font-medium text-amber-900 dark:text-amber-300 mb-1">Превышение норматива суточных</h4>
                <p className="text-sm text-amber-700 dark:text-amber-400">
                  Сверхнормативная часть суточных ({formatCurrency(results.perDiemExcess)}) облагается НДФЛ по ставке 13%.
                  Удержан налог в размере {formatCurrency(results.incomeTax)}.
                </p>
              </div>
            </div>
          </div>
        )}
      </div>
    </ToolResult>
  );
};

export default ResultsOverview;